import { en } from "../../../features/localization/translations.js";
import { authOptions } from "./auth-options.js";
import { objectEntries } from "@scalar/helpers/object/object-entries";
import { getResolvedRef } from "@scalar/workspace-store/helpers/get-resolved-ref";
import { generateHash } from "@scalar/helpers/string/generate-hash";
import { isHidden } from "@scalar/workspace-store/helpers/is-hidden";
//#region src/v2/blocks/scalar-auth-selector-block/helpers/security-scheme.ts
var requirementSignature = (requirement) => JSON.stringify(Object.keys(requirement));
/**
* Format a scheme object into a display object
*
* We also add a hash to the id to ensure it is unique across
* multiple requirements of the same scheme with different scopes
*/
var formatScheme = ({ name, value }) => ({
	id: generateHash(JSON.stringify(value)),
	label: name,
	value,
	isDeletable: true
});
/** Formats complex security schemes */
var formatComplexScheme = (scheme) => formatScheme({
	name: Object.keys(scheme).join(" & "),
	value: scheme
});
/**
* Formats a security requirement into a SecuritySchemeOption.
* Handles both simple (single key) and complex (multiple keys) auth schemes.
*/
var formatSecurityRequirement = (requirement, securitySchemes) => {
	const keys = Object.keys(requirement);
	if (keys.length > 1) {
		if (keys.some((key) => {
			const scheme = getResolvedRef(securitySchemes[key]);
			return scheme ? isHidden(scheme) : false;
		})) return;
		return formatComplexScheme(requirement);
	}
	if (keys[0]) {
		const scheme = getResolvedRef(securitySchemes[keys[0]]);
		if (!scheme || isHidden(scheme)) return;
		return formatScheme({
			name: keys[0],
			value: requirement
		});
	}
};
/**
* Finds the oauth2 scheme best suited to power a bearer scheme's "Authorize via OAuth2" shortcut:
* the first scheme with an interactive grant (authorization code / implicit), the kind that sends
* a user to an IdP login. Prefers the authorization-code grant (which can refresh) over one
* offering only implicit, so the more capable flow wins even when it is declared later.
*
* This only surfaces a convenience shortcut on the bearer form — it never hides the oauth2 scheme,
* which stays independently selectable in the auth dropdown.
*/
var getOauth2AcquisitionTarget = (securitySchemes) => {
	let implicitFallback = null;
	for (const [name, schemeRef] of Object.entries(securitySchemes)) {
		const scheme = getResolvedRef(schemeRef);
		if (scheme?.type !== "oauth2" || isHidden(scheme)) continue;
		const flows = scheme.flows;
		if (!flows) continue;
		const scopes = scheme["x-default-scopes"] ?? [];
		if (flows.authorizationCode && !isHidden(flows.authorizationCode)) return {
			name,
			flows,
			flowType: "authorizationCode",
			scopes
		};
		if (flows.implicit && !isHidden(flows.implicit) && !implicitFallback) implicitFallback = {
			name,
			flows,
			flowType: "implicit",
			scopes
		};
	}
	return implicitFallback;
};
/**
* Generates the options for the security scheme combobox
*
* Contains either a flat list, or different groups of required, available, and add new
*/
var getSecuritySchemeOptions = (security, securitySchemes, selectedSchemes, canAddNewAuth = false, labels = en) => {
	const selectedByRequirement = new Map(selectedSchemes.map((selectedScheme) => [requirementSignature(selectedScheme), selectedScheme]));
	/**
	* Build required schemes formatted as options and track scheme names in a single pass.
	* We use names (not full IDs) because we want to exclude any scheme that is already
	* required, regardless of its specific scopes or hash.
	*/
	const { requiredFormatted, requiredSchemeNames, existingIds } = security.reduce((acc, requirement) => {
		const formatted = formatSecurityRequirement(selectedByRequirement.get(requirementSignature(requirement)) ?? requirement, securitySchemes);
		if (formatted) {
			acc.requiredFormatted.push(formatted);
			acc.existingIds.add(formatted.id);
		}
		for (const name of Object.keys(requirement)) acc.requiredSchemeNames.add(name);
		return acc;
	}, {
		requiredFormatted: [],
		requiredSchemeNames: /* @__PURE__ */ new Set(),
		existingIds: /* @__PURE__ */ new Set()
	});
	const availableFormatted = [];
	for (const [name, schemeRef] of Object.entries(securitySchemes)) {
		if (requiredSchemeNames.has(name)) continue;
		const scheme = getResolvedRef(schemeRef);
		if (scheme && !isHidden(scheme)) {
			const formatted = formatScheme({
				name,
				value: selectedByRequirement.get(requirementSignature({ [name]: [] })) ?? { [name]: scheme.type === "oauth2" ? [...scheme["x-default-scopes"] ?? []] : [] }
			});
			availableFormatted.push(formatted);
			existingIds.add(formatted.id);
		}
	}
	/**
	* Add selected schemes to available if they do not already exist
	* This ensures that selected schemes with specific scopes are always available as options
	*/
	for (const selectedScheme of selectedSchemes) {
		const formatted = formatSecurityRequirement(selectedScheme, securitySchemes);
		if (formatted && !existingIds.has(formatted.id)) {
			availableFormatted.push(formatted);
			existingIds.add(formatted.id);
		}
	}
	const options = [{
		label: labels.authSelector.requiredGroup,
		options: requiredFormatted
	}, {
		label: labels.authSelector.availableGroup,
		options: availableFormatted
	}];
	if (!canAddNewAuth) return requiredFormatted.length ? options : availableFormatted;
	options.push({
		label: labels.authSelector.addGroup,
		options: objectEntries(authOptions).map(([key, value]) => ({
			id: key,
			label: labels.authOptions[key],
			value: { [key]: [] },
			payload: value.payload,
			isDeletable: false
		}))
	});
	return options;
};
//#endregion
export { formatComplexScheme, formatScheme, getOauth2AcquisitionTarget, getSecuritySchemeOptions };

//# sourceMappingURL=security-scheme.js.map